import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import EditIcon from '@mui/icons-material/Edit';
import {
  MdArrowBack,
  MdAdd,
  MdExpandMore,
  MdExpandLess,
  MdSave,
  MdDownload,
  MdInfo
} from 'react-icons/md';
import { useFetchDataId, useFetchData, useCreateData, useUpdateData } from 'services/queries';
import { useAPI } from 'services/useAPI';
import { formatCurrency } from 'utils/formatters';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PaymentStatusChip } from './PaymentStatusChip';
import DynamicFormField from 'components/form/DynamicFormField';
import OverrideConfirmDialog from './components/OverrideConfirmDialog';
import StalePaymentDialog from './components/StalePaymentDialog';
import {
  PAYMENT_STATUS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_CATEGORY,
  PAYMENT_CATEGORY_LABELS,
  PAYMENT_CATEGORY_OPTIONS,
  PAID_STATUSES,
  PAYMENT_ENDPOINTS,
  PAYMENT_MESSAGES,
  DEFAULT_PAYMENT_FORM,
  FILE_UPLOAD_CONFIG,
  OVERRIDE_DIALOG_MESSAGES,
} from './constants';
import { formatDateBrazilian } from 'utils/dateUtils';
import { validateFile, formatFormData } from './PaymentValidation';
import {
  getOverrideDisabledReason,
  isValueFieldDisabled,
  isTemplateControlledFieldDisabled,
  isDueDateFieldDisabled,
  OVERRIDDEN_FIELD_SX,
} from './utils/paymentOverrideUtils';

const OverrideButton = ({ disabledReason, onOverride }) => {
  const btn = (
    <span>
      <Button
        variant="outlined"
        size="small"
        startIcon={<EditIcon />}
        onClick={(e) => {
          e.stopPropagation();
          onOverride();
        }}
        disabled={!!disabledReason}
        sx={{
          borderColor: '#1976d2',
          color: '#1976d2',
          '&.Mui-disabled': {
            borderColor: '#d1d5db',
            color: '#9ca3af',
          },
        }}
      >
        Edit Values Manually
      </Button>
    </span>
  );

  if (disabledReason) {
    return <Tooltip title={disabledReason}>{btn}</Tooltip>;
  }
  return btn;
};

const OrderPaymentDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentRefs = useRef({});

  const [expandedPayment, setExpandedPayment] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [formData, setFormData] = useState({
    ...DEFAULT_PAYMENT_FORM,
    date: null,
    sales_order: orderId  // Include sales_order from the start
  });
  const [errors, setErrors] = useState({});

  // Fetch order details
  const { data: order, isLoading: loadingOrder } = useFetchDataId(
    '/order/sales-order',
    orderId
  );

  // Fetch payments for this order
  const { data: payments = [], isLoading: loadingPayments, refetch: refetchPayments } = useFetchData(
    `/payment/?sales_order=${orderId}`
  );

  // Check if payments are stale (invoice total changed since last calculation)
  const { data: stalenessData, refetch: refetchStaleness } = useFetchData(
    PAYMENT_ENDPOINTS.CHECK_STALENESS(orderId),
    false, // showToast = false — don't show error toast for staleness check
    !!orderId // enabled — don't fire until orderId is defined
  );
  const [staleDialogDismissed, setStaleDialogDismissed] = useState(false);

  const showStaleDialog = stalenessData?.is_stale && !staleDialogDismissed;

  // Mutations
  const createMutation = useCreateData(PAYMENT_ENDPOINTS.BASE, ['payment'], {
    hasFiles: true,  // Enable multipart/form-data for file uploads
    showNotification: true,
    onSuccess: () => {
      refetchPayments();
      setShowCreateForm(false);
      resetForm();
      toast.success(PAYMENT_MESSAGES.CREATE_SUCCESS);
    },
    onError: (error) => {
      if (error.response?.data) {
        setErrors(error.response.data);
        const errorMsg = error.response.data.non_field_errors ||
                        error.response.data.detail ||
                        'Failed to create payment';
        toast.error(errorMsg);
      }
    }
  });

  const updateMutation = useUpdateData(PAYMENT_ENDPOINTS.BASE, ['payment'], {
    hasFiles: true,  // Enable multipart/form-data for file uploads
    showNotification: true,
    onSuccess: () => {
      refetchPayments();
      setEditingPayment(null);
      setExpandedPayment(null);
      resetForm();
      toast.success(PAYMENT_MESSAGES.UPDATE_SUCCESS);
    },
    onError: (error) => {
      if (error.response?.data) {
        setErrors(error.response.data);
        const errorMsg = error.response.data.non_field_errors ||
                        error.response.data.detail ||
                        'Failed to update payment';
        toast.error(errorMsg);
      }
    }
  });

  // --- Value Override ---
  const api = useAPI();
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const overrideInFlight = useRef(false);

  const handleOverrideConfirm = useCallback(async () => {
    if (!editingPayment || overrideInFlight.current) return;
    overrideInFlight.current = true;
    setOverrideLoading(true);
    try {
      const response = await api.post(
        PAYMENT_ENDPOINTS.OVERRIDE_VALUES(editingPayment.id)
      );
      toast.success(PAYMENT_MESSAGES.OVERRIDE_SUCCESS);
      setOverrideDialogOpen(false);

      // Keep row expanded — update editingPayment with fresh data from response
      if (response.data) {
        setEditingPayment(response.data);
        setFormData(prev => ({
          ...prev,
          status: response.data.status,
          usd_value: response.data.usd_value,
          brl_value: response.data.brl_value,
          dollar_exchange: response.data.dollar_exchange,
        }));
      }

      // Refetch in background for table consistency, but don't collapse
      refetchPayments();
    } catch (error) {
      const detail = error.response?.data?.detail || PAYMENT_MESSAGES.OVERRIDE_ERROR;
      toast.error(detail);
      setOverrideDialogOpen(false);
    } finally {
      setOverrideLoading(false);
      overrideInFlight.current = false;
    }
  }, [editingPayment, api, refetchPayments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate payments using useCreateData from queries.js
  const generatePayments = useCreateData(
    `${PAYMENT_ENDPOINTS.GENERATE}/${orderId}`,
    ['payment', 'orders'],
    {
      showNotification: false,
      onSuccess: (data) => {
        toast.success(data.message || PAYMENT_MESSAGES.GENERATE_SUCCESS);
        refetchPayments();
        refetchStaleness();
      }
    }
  );

  // Recalculate payments using useCreateData from queries.js
  const recalculatePayments = useCreateData(
    `${PAYMENT_ENDPOINTS.RECALCULATE}/${orderId}`,
    ['payment', 'orders'],
    {
      showNotification: false,
      onSuccess: (data) => {
        toast.success(data.message || PAYMENT_MESSAGES.RECALCULATE_SUCCESS);
        setStaleDialogDismissed(true);
        refetchPayments();
        refetchStaleness();
      }
    }
  );

  const handleStaleRecalculate = () => {
    recalculatePayments.mutate({}, {
      onError: (error) => {
        toast.error(
          error?.response?.data?.error || PAYMENT_MESSAGES.RECALCULATE_ERROR
        );
      }
    });
  };

  // Determine if payments exist and button configuration
  const hasTemplatePayments = useMemo(() =>
    payments.some(p => p.payment_term_component),
    [payments]
  );

  const buttonConfig = useMemo(() => ({
    label: hasTemplatePayments ? PAYMENT_MESSAGES.RECALCULATE_BTN : PAYMENT_MESSAGES.GENERATE_BTN,
    disabled: !order?.payment_term_template,
    mutation: hasTemplatePayments ? recalculatePayments : generatePayments,
  }), [hasTemplatePayments, order?.payment_term_template, generatePayments, recalculatePayments]);

  const isGeneratingOrRecalculating = generatePayments.isLoading || recalculatePayments.isLoading;

  const handleGeneratePayments = () => {
    // useCreateData expects an object, pass empty object for action endpoints
    buttonConfig.mutation.mutate({});
  };

  // Calculate payment summary
  const summary = useMemo(() => {
    if (!payments) {
      return {
        progress: 0,
        totalPaidUsd: 0,
        remainingUsd: 0,
        totalValueUsd: 0,
        totalPaidBrl: 0,
        totalValueBrl: 0,
        remainingBrl: 0
      };
    }

    const activePayments = payments.filter(p => p.status !== PAYMENT_STATUS.CANCELLED);
    const paidPayments = payments.filter(p => PAID_STATUSES.includes(p.status));

    const totalPaymentsUsd = activePayments.reduce((sum, p) => sum + parseFloat(p.usd_value || 0), 0);
    const totalPaymentsBrl = activePayments.reduce((sum, p) => sum + parseFloat(p.brl_value || 0), 0);
    const totalPaidUsd = paidPayments.reduce((sum, p) => sum + parseFloat(p.usd_value || 0), 0);
    const totalPaidBrl = paidPayments.reduce((sum, p) => sum + parseFloat(p.brl_value || 0), 0);

    const remainingUsd = totalPaymentsUsd - totalPaidUsd;
    const remainingBrl = totalPaymentsBrl - totalPaidBrl;
    const progress = totalPaymentsUsd > 0 ? (totalPaidUsd / totalPaymentsUsd) * 100 : 0;

    return {
      progress: Math.min(progress, 100),
      totalPaidUsd,
      remainingUsd: Math.max(remainingUsd, 0),
      totalValueUsd: totalPaymentsUsd,
      totalPaidBrl,
      totalValueBrl: totalPaymentsBrl,
      remainingBrl: Math.max(remainingBrl, 0),
      totalPaymentsUsd,
      totalPaymentsBrl
    };
  }, [payments]);

  // Handle payment focus from URL parameter
  useEffect(() => {
    const targetPaymentId = searchParams.get('paymentId');

    if (targetPaymentId && payments.length > 0) {
      // Find the payment with the matching payment_id
      const targetPayment = payments.find(p => p.payment_id === parseInt(targetPaymentId));

      if (targetPayment) {
        // Expand the payment
        setExpandedPayment(targetPayment.id);
        handleEdit(targetPayment);

        // Scroll to the payment row after a short delay to ensure rendering
        setTimeout(() => {
          const element = paymentRefs.current[targetPayment.id];
          if (element) {
            // Get the position of the element and scroll it to near the top of the viewport
            const rect = element.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = rect.top + scrollTop - 100; // 100px offset from the top

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });

            // Add a visual highlight effect
            element.style.backgroundColor = '#fff3cd';
            setTimeout(() => {
              element.style.transition = 'background-color 2s ease-out';
              element.style.backgroundColor = '';
            }, 1000);
          }
        }, 100);
      }
    }
  }, [payments, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove smart defaults - form should be empty
  const initializeEmptyForm = () => {
    setFormData({
      contract_number: '',
      intermediary: '',
      date: null,
      due_date: null,
      brl_value: null,
      usd_value: null,
      dollar_exchange: null,
      status: PAYMENT_STATUS.TO_BE_ISSUED,
      percentage: null,
      file: null,
      sales_order: orderId  // Always include sales_order
    });
  };

  useEffect(() => {
    if (showCreateForm) {
      initializeEmptyForm();
    }
  }, [showCreateForm, orderId]);

  const resetForm = () => {
    setFormData({
      ...DEFAULT_PAYMENT_FORM,
      date: null,
      sales_order: orderId  // Always include sales_order
    });
    setErrors({});
  };

  const togglePaymentExpand = (payment) => {
    if (expandedPayment === payment.id) {
      setExpandedPayment(null);
      setEditingPayment(null);
      resetForm();
    } else {
      setExpandedPayment(payment.id);
      handleEdit(payment);
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      contract_number: payment.contract_number || '',
      intermediary: payment.intermediary || '',
      date: payment.date || '',
      due_date: payment.due_date || '',
      brl_value: payment.brl_value || '',
      usd_value: payment.usd_value || '',
      dollar_exchange: payment.dollar_exchange || '',
      status: payment.status || PAYMENT_STATUS.TO_BE_ISSUED,
      percentage: payment.percentage || '',
      category: payment.category || null,
      file: null
    });
  };

  const handleChange = (field) => (event) => {
    const value = event?.target ? event.target.value : event;

    // Special handling for file field
    if (field === 'file' && value instanceof File) {
      const validation = validateFile(value);

      if (!validation.valid) {
        setErrors(prev => ({
          ...prev,
          file: validation.error
        }));
        return;
      }

      // Clear any previous file errors if validation passes
      if (errors.file) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.file;
          return newErrors;
        });
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field (except file which is handled above)
    if (field !== 'file' && errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    // Validation: Check for manual invoice payment when template exists
    if (!editingPayment?.is_system_managed &&
        formData.category === PAYMENT_CATEGORY.INVOICE &&
        order?.payment_term_template) {
      toast.error('Manual invoice payments are not allowed when order uses a payment template');
      setErrors({ category: 'Manual invoice payments are not allowed when using payment templates' });
      return;
    }

    // Ensure sales_order is included and format the data properly
    const dataToSubmit = {
      ...formData,
      sales_order: orderId
    };

    // Format dates properly for the API
    const submitData = formatFormData(dataToSubmit, editingPayment);

    // Ensure sales_order is always present
    if (!submitData.sales_order) {
      submitData.sales_order = orderId;
    }

    try {
      if (editingPayment) {
        await updateMutation.mutateAsync({
          id: editingPayment.id,
          newData: submitData
        });
      } else {
        await createMutation.mutateAsync(submitData);
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    }
  };

  // Whether the currently-editing payment has been overridden.
  // Uses value_overridden_at (timestamp) instead of value_overridden_by (user FK)
  // because value_overridden_by is SET_NULL on user deletion, which would
  // incorrectly re-lock fields and show the override button again.
  const isValueOverridden = !!editingPayment?.value_overridden_at;

  // Field group identifiers for 3-group layout (Contract Details, Values, Attachments)
  const VALUE_FIELD_IDS = ['values_section', 'usd_value', 'brl_value', 'dollar_exchange', 'percentage'];
  const ATTACHMENT_FIELD_IDS = ['file_section', 'file'];

  // Payment form fields with sections - reorganized per mockup
  const formFields = [
    // --- Section 1: Contract & Payment Details ---
    {
      id: 'contract_section',
      type: 'section',
      title: 'Contract & Payment Details',
      mdColSpan: 12,
    },
    {
      id: 'contract_number',
      label: 'Contract Number',
      type: 'text',
      value: formData.contract_number,
      handleInputChange: handleChange('contract_number'),
      mdColSpan: 4,
      error: errors.contract_number,
    },
    {
      id: 'intermediary',
      label: 'Intermediary',
      type: 'text',
      value: formData.intermediary,
      handleInputChange: handleChange('intermediary'),
      mdColSpan: 4,
      error: errors.intermediary,
    },
    {
      id: 'category',
      label: 'Category',
      type: 'select',
      value: formData.category,
      handleInputChange: handleChange('category'),
      mdColSpan: 4,
      error: errors.category,
      options: PAYMENT_CATEGORY_OPTIONS,
      disabled: isTemplateControlledFieldDisabled(editingPayment),
    },
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      value: formData.status,
      handleInputChange: handleChange('status'),
      mdColSpan: 4,
      required: true,
      error: errors.status,
      options: PAYMENT_STATUS_OPTIONS,
    },
    {
      id: 'due_date',
      label: 'Due Date',
      type: 'date',
      value: formData.due_date,
      handleInputChange: handleChange('due_date'),
      mdColSpan: 4,
      required: true,
      error: errors.due_date,
      disabled: isDueDateFieldDisabled(editingPayment),
    },
    {
      id: 'date',
      label: 'Payment Date',
      type: 'date',
      value: formData.date,
      handleInputChange: handleChange('date'),
      mdColSpan: 4,
      required: false,
      error: errors.date,
    },

    // --- Section 2: Values ---
    {
      id: 'values_section',
      type: 'section',
      title: 'Values',
      mdColSpan: 12,
    },
    {
      id: 'usd_value',
      label: 'USD ($)',
      type: 'number',
      value: formData.usd_value,
      handleInputChange: handleChange('usd_value'),
      mdColSpan: 3,
      error: errors.usd_value,
      inputProps: { min: 0, step: 0.01 },
      // System-managed payments: locked until override. Manual payments: always editable.
      disabled: isValueFieldDisabled(editingPayment, isValueOverridden),
    },
    {
      id: 'brl_value',
      label: 'BRL (R$)',
      type: 'number',
      value: formData.brl_value,
      handleInputChange: handleChange('brl_value'),
      mdColSpan: 3,
      required: true,
      error: errors.brl_value,
      inputProps: { min: 0, step: 0.01 },
      disabled: isValueFieldDisabled(editingPayment, isValueOverridden),
    },
    {
      id: 'dollar_exchange',
      label: 'Exchange Rate (USD\u2192BRL)',
      type: 'number',
      value: formData.dollar_exchange,
      handleInputChange: handleChange('dollar_exchange'),
      mdColSpan: 3,
      error: errors.dollar_exchange,
      inputProps: { step: 0.00001, min: 0 },
      helperText: 'Dollar to Real exchange rate',
      // Exchange rate is ALWAYS editable
    },
    {
      id: 'percentage',
      label: '%',
      type: 'number',
      value: formData.percentage,
      handleInputChange: handleChange('percentage'),
      mdColSpan: 3,
      error: errors.percentage,
      inputProps: { step: 0.01, min: 0, max: 100 },
      // Percentage is ALWAYS read-only (template property)
      disabled: true,
    },

    // --- Section 3: Attachments ---
    {
      id: 'file_section',
      type: 'section',
      title: 'Attachments',
      mdColSpan: 12,
    },
    {
      id: 'file',
      label: 'Upload Payment Document',
      type: 'file',
      value: formData.file,
      handleInputChange: handleChange('file'),
      mdColSpan: 12,
      error: errors.file,
      helperText: PAYMENT_MESSAGES.FILE_UPLOAD_HELP,
      accept: FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(','),
      currentFile: editingPayment?.file_url,
    },
  ];


  if (loadingOrder || loadingPayments) return <div>Loading...</div>;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              component="button"
              onClick={() => navigate('/admin/pm-main/payments')}
              sx={{ cursor: 'pointer' }}
            >
              Payments
            </Link>
            <Link
              underline="hover"
              color="inherit"
              component="button"
              onClick={() => navigate('/admin/payment/orders')}
              sx={{ cursor: 'pointer' }}
            >
              Orders
            </Link>
            <Typography color="text.primary" aria-current="page">
              {order?.order_identifier}
            </Typography>
          </Breadcrumbs>

          <IconButton onClick={() => navigate(-1)} size="small">
            <MdArrowBack />
          </IconButton>
        </Box>
      </Box>

      {/* Order Summary Card - Full Width */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
                <Grid xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order Information
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {order?.order_identifier}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order?.client?.name || order?.client_name}
                  </Typography>
                  <Stack direction="column" spacing={1} alignItems="flex-start">
                    <Chip
                      label={
                        typeof order?.status === 'object'
                          ? order.status?.status || order.status?.name
                          : order?.status
                      }
                      size="small"
                    />
                  </Stack>
                </Grid>

                {/* Expanded to occupy remaining width for better UX */}
                <Grid xs={12} md={9}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Progress
                    </Typography>
                    <Tooltip title="Progress calculated as: Paid payments / Order total value. Only payments with status 'PAID' count toward progress.">
                      <MdInfo size={16} style={{ color: '#666' }} />
                    </Tooltip>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="h5" fontWeight="bold">
                      {summary.progress.toFixed(1)}%
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(summary.totalPaidUsd, 'USD')} / {formatCurrency(summary.totalValueUsd, 'USD')}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={summary.progress}
                    sx={{
                      height: 10,
                      borderRadius: 1,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          summary.progress >= 100 ? '#4caf50' :
                          summary.progress > 0 ? '#ff9800' : '#2196f3'
                      }
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      Paid: {formatCurrency(summary.totalPaidUsd, 'USD')} ({payments.filter(p => PAID_STATUSES.includes(p.status)).length} payments)
                    </Typography>
                    <Typography variant="caption" color="error">
                      Remaining: {formatCurrency(summary.remainingUsd, 'USD')}
                    </Typography>
                  </Box>
                </Grid>

              </Grid>
            </CardContent>
          </Card>

      <Grid container spacing={3}>
        {/* Create Payment Form */}
        <Collapse in={showCreateForm} sx={{ width: '100%' }}>
          <Grid size={12}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" gutterBottom>
                Create New Payment
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              {errors.non_field_errors && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {Array.isArray(errors.non_field_errors) ? errors.non_field_errors.join(' ') : errors.non_field_errors}
                </Alert>
              )}

              <Grid container spacing={1.5}>
                {formFields.map((field) => (
                  <DynamicFormField
                    key={field.id}
                    field={field}
                    formData={formData}
                  />
                ))}
              </Grid>

              <Box sx={{ mt: 2.5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCreateForm(false);
                    initializeEmptyForm();
                    setErrors({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<MdSave />}
                  onClick={handleSubmit}
                  disabled={createMutation.isLoading}
                >
                  {createMutation.isLoading ? 'Creating...' : 'Create Payment'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Collapse>

        {/* Payments List */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Payments ({payments.length})
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGeneratePayments}
                    disabled={buttonConfig.disabled || isGeneratingOrRecalculating}
                    startIcon={isGeneratingOrRecalculating && <CircularProgress size={20} color="inherit" />}
                    title={
                      !order?.payment_term_template
                        ? PAYMENT_MESSAGES.NO_TEMPLATE
                        : buttonConfig.label
                    }
                  >
                    {isGeneratingOrRecalculating ? PAYMENT_MESSAGES.PROCESSING : buttonConfig.label}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<MdAdd />}
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    disabled={showCreateForm}
                  >
                    Add Payment
                  </Button>
                </Stack>
              </Box>

              {payments.length === 0 ? (
                <Alert severity="info">
                  No payments have been created for this order yet.
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={50}></TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Contract</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell align="right">USD</TableCell>
                        <TableCell align="right">BRL</TableCell>
                        <TableCell align="right">%</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments
                          .sort((a, b) => (a.payment_id || 0) - (b.payment_id || 0))
                        .map((payment) => (
                          <React.Fragment key={payment.id}>
                            <TableRow
                              ref={el => paymentRefs.current[payment.id] = el}
                              hover
                              onClick={() => togglePaymentExpand(payment)}
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: editingPayment?.id === payment.id ? '#f5f5f5' : 'inherit',
                                '&:hover': {
                                  backgroundColor: '#f0f0f0'
                                }
                              }}
                            >
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePaymentExpand(payment);
                                  }}
                                >
                                  {expandedPayment === payment.id ?
                                    <MdExpandLess /> : <MdExpandMore />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{payment.payment_id || '-'}</TableCell>
                              <TableCell>
                                <PaymentStatusChip
                                  status={payment.status}
                                  effective_status={payment.effective_status}
                                  is_overdue={payment.is_overdue}
                                  paid_late={payment.paid_late}
                                  days_late={payment.days_late}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                                  <Chip
                                    label={payment.category ? PAYMENT_CATEGORY_LABELS[payment.category] || payment.category_display || 'Uncategorized' : 'Uncategorized'}
                                    size="small"
                                    sx={{
                                      backgroundColor: payment.category ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                      color: payment.category ? '#1976d2' : '#666',
                                      fontWeight: 500,
                                      fontSize: '0.875rem'
                                    }}
                                  />
                                  {payment.category === PAYMENT_CATEGORY.INVOICE && payment.payment_term_component_full_display && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                                      {payment.payment_term_component_full_display}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>{payment.contract_number || '-'}</TableCell>
                              <TableCell>
                                {formatDateBrazilian(payment.due_date)}
                              </TableCell>
                              <TableCell align="right">
                                <Box display="inline-flex" alignItems="center">
                                  {payment.usd_value
                                    ? formatCurrency(payment.usd_value, 'USD')
                                    : '-'}
                                  {payment.value_overridden_at && (
                                    <Tooltip
                                      title={`Values manually set${payment.value_overridden_by ? ` by ${payment.value_overridden_by.full_name || payment.value_overridden_by.email}` : ''} on ${dayjs(payment.value_overridden_at).format('DD/MM/YYYY')}`}
                                    >
                                      <EditIcon
                                        sx={{ fontSize: 14, color: '#1976d2', ml: 0.5, cursor: 'help' }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {payment.brl_value
                                  ? formatCurrency(payment.brl_value, 'BRL')
                                  : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {payment.percentage ? `${payment.percentage}%` : '-'}
                              </TableCell>
                              <TableCell align="center">
                                <Box display="flex" gap={0.5} justifyContent="center">
                                  {payment.file_url && (
                                    <Tooltip title="Download Document">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(payment.file_url, '_blank');
                                        }}
                                      >
                                        <MdDownload size={18} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>

                            {/* Expanded Edit Form */}
                            <TableRow>
                            <TableCell colSpan={10} sx={{ p: 0 }}>
                                <Collapse in={expandedPayment === payment.id}>
                                  <Box sx={{ p: 2.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                    {/* Form header — title only, no override UI */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                      <Typography variant="h6" fontWeight={700}>
                                        Edit Payment
                                      </Typography>
                                    </Box>

                                    {errors.non_field_errors && (
                                      <Alert severity="error" sx={{ mb: 2 }}>
                                        {Array.isArray(errors.non_field_errors) ? errors.non_field_errors.join(' ') : errors.non_field_errors}
                                      </Alert>
                                    )}

                                    {/* Group 1: Contract & Payment Details (exclude values and attachments) */}
                                    <Grid container spacing={1.5}>
                                      {formFields
                                        .filter(f => !VALUE_FIELD_IDS.includes(f.id) && !ATTACHMENT_FIELD_IDS.includes(f.id))
                                        .map((field) => (
                                          <DynamicFormField
                                            key={field.id}
                                            field={field}
                                            formData={formData}
                                          />
                                        ))}
                                    </Grid>

                                    {/* Group 2: Values section with integrated override button */}
                                    <Box sx={{ mt: 2 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" color="primary">Values</Typography>
                                        {/* Override button or audit trail — only for system-managed payments */}
                                        {(editingPayment?.is_system_managed || editingPayment?.is_auto_generated || editingPayment?.payment_term_component) && (
                                          editingPayment?.value_overridden_at ? (
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: '#1976d2',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                              }}
                                            >
                                              <EditIcon sx={{ fontSize: 14 }} />
                                              Amounts set manually
                                              {editingPayment.value_overridden_by
                                                ? (<>{' '}by{' '}<strong style={{ margin: '0 3px' }}>
                                                    {editingPayment.value_overridden_by.full_name
                                                      || editingPayment.value_overridden_by.email}
                                                  </strong></>)
                                                : null}
                                              {' '}&mdash;{' '}
                                              {dayjs(editingPayment.value_overridden_at).format('DD/MM/YYYY')}
                                            </Typography>
                                          ) : (
                                            <OverrideButton
                                              disabledReason={getOverrideDisabledReason(editingPayment || payment, payments)}
                                              onOverride={() => {
                                                const config = OVERRIDE_DIALOG_MESSAGES[(editingPayment || payment).status];
                                                if (!config) {
                                                  toast.error(`Override is not available for payments with status "${payment.status}".`);
                                                  return;
                                                }
                                                setOverrideDialogOpen(true);
                                              }}
                                            />
                                          )
                                        )}
                                      </Box>
                                      <Grid container spacing={1.5}>
                                        {formFields
                                          .filter(f => VALUE_FIELD_IDS.includes(f.id) && f.type !== 'section')
                                          .map((field) => {
                                            const needsHighlight = isValueOverridden
                                              && (field.id === 'usd_value' || field.id === 'brl_value');
                                            return needsHighlight ? (
                                              <Box key={field.id} sx={{ ...OVERRIDDEN_FIELD_SX, display: 'contents' }}>
                                                <DynamicFormField field={field} formData={formData} />
                                              </Box>
                                            ) : (
                                              <DynamicFormField key={field.id} field={field} formData={formData} />
                                            );
                                          })}
                                      </Grid>
                                    </Box>

                                    {/* Group 3: Attachments */}
                                    <Grid container spacing={1.5} sx={{ mt: 2 }}>
                                      {formFields
                                        .filter(f => ATTACHMENT_FIELD_IDS.includes(f.id))
                                        .map((field) => (
                                          <DynamicFormField
                                            key={field.id}
                                            field={field}
                                            formData={formData}
                                          />
                                        ))}
                                    </Grid>

                                    <Divider sx={{ mt: 2.5, mb: 2 }} />

                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                      <Button
                                        variant="outlined"
                                        onClick={() => {
                                          setExpandedPayment(null);
                                          setEditingPayment(null);
                                          resetForm();
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={updateMutation.isLoading}
                                      >
                                        {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
                                      </Button>
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stale Payment Detection Dialog */}
      <StalePaymentDialog
        open={showStaleDialog}
        onDismiss={() => setStaleDialogDismissed(true)}
        onRecalculate={handleStaleRecalculate}
        isLoading={recalculatePayments.isLoading}
        currentTotal={stalenessData?.current_invoice_total}
        paymentTotal={stalenessData?.payment_total}
      />

      {/* Override Confirmation Dialog */}
      <OverrideConfirmDialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        onConfirm={handleOverrideConfirm}
        dialogConfig={
          editingPayment
            ? OVERRIDE_DIALOG_MESSAGES[editingPayment.status]
            : null
        }
        isLoading={overrideLoading}
      />
    </Box>
  );
};

export default OrderPaymentDetails;